import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import {
  Brain, TrendingUp, ClipboardList, AlertTriangle, ShieldOff, Package, Clock,
  Handshake, BarChart3, Loader2, Info, RefreshCcw, Search, ArrowUp, ArrowDown,
  Activity, Target, ShoppingCart, Percent, ThumbsUp, Users, Zap, Gauge,
} from 'lucide-react';

const RANGES = [['today', 'Today'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days']];
const TABS = [['overview', 'Overview'], ['swot', 'Investigation (SWOT)'], ['seller', 'Seller Brain']];

const KPI_ICONS = {
  total_opportunities: Target, high_intent: Zap, ai_assisted_orders: ShoppingCart,
  conversion: Percent, recommendation_success: ThumbsUp, seller_handoffs: Users,
  ai_issues: AlertTriangle,
};

const STATUS_STYLE = {
  'Healthy': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Needs Attention': 'bg-amber-50 text-amber-700 border-amber-200',
  'Investigate': 'bg-red-50 text-red-700 border-red-200',
};
const HEALTH_BADGE = {
  'HEALTHY': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'NEEDS ATTENTION': 'bg-amber-100 text-amber-700 border-amber-200',
  'INVESTIGATE': 'bg-red-100 text-red-700 border-red-200',
};

// Exactly four seller statuses.
const SELLER_STATUS = {
  'GOOD': { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'WATCH': { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  'NEEDS ATTENTION': { dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
  'NO ACTIVITY': { dot: 'bg-neutral-300', chip: 'bg-neutral-100 text-neutral-500 border-neutral-200' },
};
const STATUS_FILTERS = ['ALL', 'GOOD', 'WATCH', 'NEEDS ATTENTION', 'NO ACTIVITY'];

function StatusBadge({ status, testid }) {
  const s = SELLER_STATUS[status] || SELLER_STATUS['NO ACTIVITY'];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.chip}`} data-testid={testid}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{status}
    </span>
  );
}

function Section({ title, icon: Icon, children, testid, right }) {
  return (
    <section className="bg-white rounded-xl border border-fab-primary-lighter/70 p-4" data-testid={testid}>
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-fab-primary" /> : null}
          <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

function Bar({ label, value, max, pct }) {
  const w = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-44 shrink-0 text-[12px] text-neutral-700 truncate">{label}</div>
      <div className="flex-1 h-2 bg-neutral-100 rounded overflow-hidden">
        <div className="h-2 bg-fab-primary rounded" style={{ width: `${w}%` }} />
      </div>
      <div className="w-24 text-right text-[12px] font-medium text-neutral-800">
        {value}{pct != null ? <span className="text-neutral-400"> ({pct}%)</span> : null}
      </div>
    </div>
  );
}

function DistList({ rows, empty = 'No data yet', showPct = false }) {
  const total = rows.reduce((s, r) => s + (r.count || 0), 0);
  const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 0);
  if (!rows.length) return <div className="text-[12px] text-neutral-500">{empty}</div>;
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <Bar key={`${r.label}-${i}`} label={r.label} value={r.count || 0} max={max}
             pct={showPct && total ? Math.round(((r.count || 0) / total) * 1000) / 10 : null} />
      ))}
    </div>
  );
}

function KpiCard({ kpi }) {
  const Icon = KPI_ICONS[kpi.key] || Activity;
  const val = kpi.value == null ? '—' : `${kpi.value}${kpi.suffix || ''}`;
  const d = kpi.delta;
  return (
    <div className="bg-white rounded-xl border border-fab-primary-lighter/70 p-3.5" data-testid={`msb-kpi-${kpi.key}`}>
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-neutral-500 font-semibold">
        <Icon className="w-3.5 h-3.5 text-fab-primary" /> {kpi.label}
      </div>
      <div className="mt-1.5 text-3xl font-bold text-neutral-900">{val}</div>
      {d ? (
        <div className={`mt-1 text-[11px] flex items-center gap-1 ${d.direction === 'down' ? 'text-red-600' : 'text-emerald-600'}`}>
          {d.direction === 'down' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
          {d.pct != null ? `${d.pct}%` : d.abs} <span className="text-neutral-400">vs prev period</span>
        </div>
      ) : <div className="mt-1 text-[11px] text-neutral-400">No prior data</div>}
    </div>
  );
}

function SwotBlock({ swot, testid = 'msb-swot', onSelect }) {
  const cards = [
    ['Strengths', swot.strengths, 'emerald'],
    ['Weaknesses', swot.weaknesses, 'red'],
    ['Opportunities', swot.opportunities, 'fab'],
    ['Risks / Threats', swot.risks, 'amber'],
  ];
  const tone = {
    emerald: 'border-emerald-200 bg-emerald-50/40', red: 'border-red-200 bg-red-50/40',
    fab: 'border-fab-primary-lighter bg-fab-primary-lighter/20', amber: 'border-amber-200 bg-amber-50/40',
  };
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-neutral-700">Sales Brain Health</span>
        <span className={`px-2.5 py-1 rounded-full text-[12px] font-semibold border ${HEALTH_BADGE[swot.overall_health] || HEALTH_BADGE['NEEDS ATTENTION']}`}
              data-testid={`${testid}-health`}>{swot.overall_health}</span>
        {swot.anomaly_count > 0 && (
          <span className="text-[12px] text-neutral-500">{swot.anomaly_count} area(s) flagged</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cards.map(([title, list, t]) => (
          <div key={title} className={`rounded-xl border p-4 ${tone[t]}`} data-testid={`${testid}-${title.split(' ')[0].toLowerCase()}`}>
            <div className="text-[12px] font-semibold text-neutral-800 uppercase tracking-wide mb-2">{title}</div>
            <ul className="space-y-1.5">
              {(list || []).map((x, i) => (
                <li key={i}>
                  <button onClick={() => onSelect?.({ category: title, ...x })}
                          data-testid={`${testid}-item`}
                          className="w-full text-left text-[13px] text-neutral-700 flex items-start gap-2 rounded-md px-1.5 py-1 hover:bg-white/70 transition-colors group">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-fab-primary shrink-0" />
                    <span className="flex-1">{x.text}</span>
                    <span className="text-[11px] text-fab-primary opacity-0 group-hover:opacity-100 shrink-0 mt-0.5">Evidence →</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecommendedActions({ actions, testid = 'msb-actions', onSelect }) {
  if (!actions?.length) {
    return (
      <div className="text-[13px] text-neutral-500 bg-white rounded-xl border border-fab-primary-lighter/70 p-4" data-testid={`${testid}-empty`}>
        No evidence-based actions for this window. Sales Brain is not surfacing objection signals with enough observations to recommend a corrective action.
      </div>
    );
  }
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {actions.map((a) => (
        <button key={a.priority} onClick={() => onSelect?.(a)}
                className="w-full text-left bg-white rounded-xl border border-fab-primary-lighter/70 p-4 hover:border-fab-primary/50 hover:shadow-sm transition-all group"
                data-testid={`${testid}-item-${a.priority}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-neutral-900">{a.badge} Priority {a.priority} — {a.problem}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">Impact: {a.impact}</span>
                {a.success_rate != null && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700">Success rate: {a.success_rate}%</span>
                )}
              </div>
              <div className="mt-1.5 text-[13px] text-neutral-600">{a.evidence}</div>
              <div className="mt-1 text-[12px] text-fab-primary font-medium">→ {a.action}</div>
            </div>
            <span className="text-[11px] text-fab-primary opacity-0 group-hover:opacity-100 shrink-0">Investigate →</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function Row({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-neutral-100 last:border-0">
      <span className="text-[12px] text-neutral-500">{label}</span>
      <span className="text-[13px] text-neutral-900 text-right font-medium">{value}</span>
    </div>
  );
}

function CmpCard({ label, c, testid }) {
  const isPct = c.kind === 'percent';
  const val = c.seller == null ? '—' : (isPct ? `${c.seller}%` : c.seller);
  const plat = c.platform == null ? '—' : (isPct ? `${c.platform}%` : c.platform);
  let rel = null, relColor = 'text-neutral-400';
  if (isPct) {
    if (c.diff_vs_platform != null) {
      rel = `${c.diff_vs_platform >= 0 ? '+' : ''}${c.diff_vs_platform}% vs platform`;
      relColor = c.direction === 'up' ? 'text-emerald-600' : 'text-red-600';
    }
  } else if (c.pct_of_platform != null) {
    rel = `${c.pct_of_platform}% of platform`;
  }
  return (
    <div className="rounded-lg border border-neutral-100 p-2.5" data-testid={`msb-seller-metric-${testid}`}>
      <div className="text-[10.5px] uppercase tracking-wide text-neutral-500 font-semibold">{label}</div>
      <div className="text-xl font-bold text-neutral-900 mt-0.5">{val}</div>
      <div className="text-[10.5px] text-neutral-400">Platform: {plat}</div>
      {rel && <div className={`text-[10.5px] font-medium ${relColor}`}>{rel}</div>}
    </div>
  );
}

function SwotSection({ title, tone, findings, onSelect, testid }) {
  const toneCls = {
    emerald: 'border-emerald-200 bg-emerald-50/40', red: 'border-red-200 bg-red-50/40',
    blue: 'border-sky-200 bg-sky-50/40', amber: 'border-amber-200 bg-amber-50/40',
  }[tone];
  const primary = (findings || [])[0];
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testid}>
      <div className="text-[12px] font-semibold text-neutral-800 uppercase tracking-wide mb-2">{title}</div>
      <ul className="space-y-1.5">
        {(findings || []).map((x, i) => (
          <li key={i}>
            <button onClick={() => onSelect({ category: title, ...x })} data-testid={`${testid}-item`}
                    className="w-full text-left text-[13px] text-neutral-700 flex items-start gap-2 rounded-md px-1.5 py-1 hover:bg-white/70 transition-colors">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-fab-primary shrink-0" />
              <span className="flex-1">{x.evidence?.sufficient === false ? <span className="italic text-neutral-500">{x.text} · Insufficient evidence</span> : x.text}</span>
            </button>
          </li>
        ))}
      </ul>
      {primary && (
        <button onClick={() => onSelect({ category: title, ...primary })} data-testid={`${testid}-showmore`}
                className="mt-2 text-[12px] font-semibold text-fab-primary hover:underline">Show More →</button>
      )}
    </div>
  );
}

function SellerSwot({ swot, onSelect }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="msb-seller-swot">
      <SwotSection title="Strengths" tone="emerald" findings={swot.strengths} onSelect={onSelect} testid="msb-seller-swot-strengths" />
      <SwotSection title="Weaknesses" tone="red" findings={swot.weaknesses} onSelect={onSelect} testid="msb-seller-swot-weaknesses" />
      <SwotSection title="Opportunities" tone="blue" findings={swot.opportunities} onSelect={onSelect} testid="msb-seller-swot-opportunities" />
      <SwotSection title="Risks / Threats" tone="amber" findings={swot.risks} onSelect={onSelect} testid="msb-seller-swot-risks" />
    </div>
  );
}

function SellerActions({ actions, onSelect }) {
  if (!actions?.length) {
    return (
      <div className="text-[13px] text-neutral-500 bg-white rounded-xl border border-fab-primary-lighter/70 p-4" data-testid="msb-seller-actions-empty">
        No evidence-based actions for this window.
      </div>
    );
  }
  const impactChip = { High: 'bg-red-50 text-red-700', Medium: 'bg-amber-50 text-amber-700', Low: 'bg-neutral-100 text-neutral-600' };
  return (
    <div className="space-y-2.5" data-testid="msb-seller-actions">
      {actions.map((a) => (
        <button key={a.priority} onClick={() => onSelect(a)} data-testid={`msb-seller-actions-item-${a.priority}`}
                className="w-full text-left bg-white rounded-xl border border-fab-primary-lighter/70 p-4 hover:border-fab-primary/50 hover:shadow-sm transition-all group">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-900">{a.problem}</div>
              <div className="mt-1 text-[13px] text-neutral-600">{a.action}</div>
              <div className="mt-2 flex items-center gap-3 flex-wrap text-[12px]">
                <span className="text-neutral-500">Current: <span className="font-semibold text-neutral-800">{a.current}</span></span>
                <span className="text-neutral-500">Platform: <span className="font-semibold text-neutral-800">{a.platform}</span></span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${impactChip[a.impact] || impactChip.Low}`}>{a.badge} Impact: {a.impact}</span>
              </div>
            </div>
            <span className="text-[12px] text-fab-primary opacity-0 group-hover:opacity-100 shrink-0 font-semibold">Show More →</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function EvidenceModal({ item, onClose }) {
  if (!item) return null;
  const isAction = item.kind === 'action';
  const ev = isAction ? (item.detail || {}) : (item.evidence || {});
  const sufficient = ev.sufficient !== false;
  const title = isAction ? `${item.badge} Priority ${item.priority} — ${item.problem}` : item.text;
  const rate = isAction ? item.success_rate : ev.rate;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="msb-evidence-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-fab-primary-lighter/70">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-fab-primary font-semibold">
              {isAction ? 'Recommended Action · Evidence' : `${item.category} · Evidence`}
            </div>
            <div className="text-[15px] font-bold text-neutral-900 mt-0.5">{title}</div>
          </div>
          <button onClick={onClose} data-testid="msb-evidence-close" className="p-1.5 rounded-lg hover:bg-neutral-100"><XIcon /></button>
        </div>
        <div className="p-5">
          {!sufficient ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800" data-testid="msb-evidence-insufficient">
              Insufficient evidence. {ev.why || 'There is not enough Sales Brain data in this window to support a specific finding.'}
            </div>
          ) : (
            <div>
              <Row label="Finding" value={isAction ? ev.problem : ev.detected} />
              <Row label="Current performance" value={isAction ? ev.current_performance : ev.current_performance} />
              <Row label="Platform benchmark" value={isAction ? ev.platform_benchmark : ev.platform_benchmark} />
              <Row label="Performance gap" value={isAction ? null : ev.performance_gap} />
              <Row label="Sales Brain evidence" value={isAction ? ev.evidence_metric : ev.metric} />
              <Row label="Observations / opportunities" value={ev.observations != null ? ev.observations : null} />
              <Row label="Success / conversion rate" value={rate != null ? `${rate}%` : (ev.current_performance ? null : 'Not applicable')} />
              {isAction
                ? <Row label="Expected improvement" value={ev.expected_improvement} />
                : <Row label={`Why classified as ${({ 'Strengths': 'a strength', 'Weaknesses': 'a weakness', 'Opportunities': 'an opportunity', 'Risks / Threats': 'a risk' }[item.category] || 'a finding')}`} value={ev.why} />}
              {!isAction && <Row label="Admin takeaway" value={ev.admin_takeaway} />}
              <Row label="AI signal" value={(ev.signals || []).join(', ') || '—'} />
              <Row label="Time window" value={ev.window} />
              {isAction && <Row label="Recommended action" value={item.action} />}
              <div className="mt-2 flex items-center gap-1.5 text-[12px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-emerald-700 font-medium">Sufficient evidence</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function XIcon() {
  return <svg className="w-4 h-4 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>;
}

export default function MasterSalesBrain() {
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swot, setSwot] = useState(null);
  const [swotLoading, setSwotLoading] = useState(false);
  const [sellers, setSellers] = useState(null);
  const [sellerQuery, setSellerQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerDetail, setSellerDetail] = useState(null);
  const [sellerLoading, setSellerLoading] = useState(false);
  const [sellerInvestigate, setSellerInvestigate] = useState(false);
  const [evidence, setEvidence] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/master-sales-brain/overview?range=${range}`); setData(data); }
    catch { setData(null); } finally { setLoading(false); }
  }, [range]);

  const loadSwot = useCallback(async () => {
    setSwotLoading(true);
    try { const { data } = await api.get(`/admin/master-sales-brain/swot?range=${range}`); setSwot(data); }
    catch { setSwot(null); } finally { setSwotLoading(false); }
  }, [range]);

  const loadSellers = useCallback(async () => {
    try { const { data } = await api.get(`/admin/master-sales-brain/sellers?range=${range}`); setSellers(data); }
    catch { setSellers(null); }
  }, [range]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === 'swot' && !swot) loadSwot(); }, [tab, swot, loadSwot]);
  useEffect(() => { if (tab === 'seller' && !sellers) loadSellers(); }, [tab, sellers, loadSellers]);
  // reset dependent caches when range changes
  useEffect(() => { setSwot(null); setSellers(null); setSellerDetail(null); setSelectedSeller(null); }, [range]);

  const openSeller = async (s) => {
    setSelectedSeller(s); setSellerLoading(true); setSellerDetail(null); setSellerInvestigate(false);
    try { const { data } = await api.get(`/admin/master-sales-brain/sellers/${s.seller_id}?range=${range}`); setSellerDetail(data); }
    catch { setSellerDetail(null); } finally { setSellerLoading(false); }
  };

  const refresh = () => {
    if (tab === 'overview') loadOverview();
    else if (tab === 'swot') loadSwot();
    else { loadSellers(); if (selectedSeller) openSeller(selectedSeller); }
  };

  const filteredSellers = useMemo(() => {
    const list = sellers?.sellers || [];
    const q = sellerQuery.trim().toLowerCase();
    return list.filter((s) =>
      (statusFilter === 'ALL' || s.performance === statusFilter) &&
      (!q || s.name.toLowerCase().includes(q)));
  }, [sellers, sellerQuery, statusFilter]);

  return (
    <div className="p-5 md:p-6 space-y-5" data-testid="master-sales-brain-page">
      {/* Page header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="w-6 h-6 text-fab-primary" />
            <h1 className="text-2xl font-bold text-neutral-900">Master Sales Brain</h1>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-fab-primary-lighter text-fab-primary">Platform Overview</span>
          </div>
          <p className="text-sm text-neutral-500 mt-1">Intelligence pipeline overview across the entire platform</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-neutral-100 rounded-lg p-1" data-testid="msb-range">
            {RANGES.map(([k, l]) => (
              <button key={k} onClick={() => setRange(k)} data-testid={`msb-range-${k}`}
                      className={`px-3 py-1 text-[12px] rounded-md ${range === k ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-600'}`}>{l}</button>
            ))}
          </div>
          <button onClick={refresh} data-testid="msb-refresh"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] text-neutral-700 hover:bg-fab-elevated">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-fab-primary-lighter/70" data-testid="msb-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`msb-tab-${k}`}
                  className={`px-4 py-2.5 text-[13px] font-semibold -mb-px border-b-2 transition-colors ${tab === k ? 'border-fab-primary text-fab-primary' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>{l}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        loading ? (
          <div className="flex items-center justify-center py-16 text-neutral-500 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading platform intelligence…</div>
        ) : !data ? (
          <div className="py-16 text-center text-neutral-500">No analytics data yet.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
              {data.kpis.map((k) => <KpiCard key={k.key} kpi={k} />)}
            </div>

            {/* AI Health */}
            <Section title="AI Health / Anomalies" icon={Gauge} testid="msb-ai-health"
                     right={data.ai_health.anomaly_count > 0
                       ? <span className="text-[12px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-semibold" data-testid="msb-anomaly-count">{data.ai_health.anomaly_count} AI issues detected</span>
                       : <span className="text-[12px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">All systems healthy</span>}>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {data.ai_health.areas.map((a) => (
                  <div key={a.area} className={`rounded-lg border px-2.5 py-2 ${STATUS_STYLE[a.status]}`} data-testid={`msb-health-${a.area.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="text-[11px] text-neutral-700 font-medium leading-tight">{a.area}</div>
                    <div className="text-[12px] font-semibold mt-0.5">{a.status}</div>
                  </div>
                ))}
              </div>
              {data.ai_health.anomaly_count > 0 && (
                <button onClick={() => setTab('swot')} data-testid="msb-health-investigate"
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fab-primary text-white text-[12px] font-semibold">
                  <Activity className="w-3.5 h-3.5" /> Investigate
                </button>
              )}
            </Section>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
              <Section title="Intent funnel" icon={TrendingUp} testid="msb-intent">
                <DistList showPct rows={Object.entries(data.funnel.by_intent).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k, count: v }))} />
              </Section>
              <Section title="Next Best Action distribution" icon={ClipboardList} testid="msb-nba">
                <DistList showPct rows={(data.nba.by_action || []).slice(0, 8).map((a) => ({ label: a.action, count: a.count }))} />
              </Section>
              <Section title="Objections" icon={AlertTriangle} testid="msb-objections"
                       right={<span className="text-[11px] text-neutral-500">Verified: {data.objections.resolved_verified_facts} · Handoff: {data.objections.seller_handoff_required}</span>}>
                <DistList showPct rows={Object.entries(data.objections.by_type).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v]) => ({ label: k, count: v }))} />
              </Section>
              <Section title="Policy usage" icon={ShieldOff} testid="msb-policies"
                       right={<span className="text-[11px] text-neutral-500">Conflicts: {data.policies.conflicts}</span>}>
                <div className="space-y-2">
                  {Object.entries(data.policies.by_type).map(([t, b]) => (
                    <div key={t} className="border border-neutral-100 rounded-lg p-2" data-testid={`msb-policy-${t}`}>
                      <div className="text-[12px] font-semibold text-neutral-800">{t.replace('_POLICY', '')}</div>
                      <div className="mt-1 grid grid-cols-4 gap-1 text-[11px] text-neutral-600">
                        <div>Checks: <b>{b.checks || 0}</b></div>
                        <div className="text-emerald-700">Applied: <b>{b.applied || 0}</b></div>
                        <div className="text-amber-700">Capped: <b>{b.capped_or_denied || 0}</b></div>
                        <div className="text-red-700">Conflict: <b>{b.conflict || 0}</b></div>
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-neutral-500">Policies enabled: {data.policies.configured.enabled} · Configured: {data.policies.configured.counts}</div>
                </div>
              </Section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
              <Section title="Recommendation performance" icon={Package} testid="msb-recs"
                       right={<span className="text-[11px] text-neutral-500">Total: {data.recommendations.total}</span>}>
                <DistList showPct rows={Object.entries(data.recommendations.by_status).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k, count: v }))} />
              </Section>
              <Section title="Follow-up pipeline" icon={Clock} testid="msb-followups"
                       right={<span className="text-[11px] text-neutral-500">Sent {data.followups.executions.sent} · Blocked {data.followups.executions.blocked}</span>}>
                <DistList rows={Object.entries(data.followups.by_status).filter(([, v]) => v > 0).map(([k, v]) => ({ label: k, count: v }))} />
                <div className="mt-2 text-[11px] text-neutral-500 flex items-start gap-1.5"><Info className="w-3 h-3 mt-0.5 shrink-0" /> Follow-up execution kill switch is OFF — planning only, no messages sent.</div>
              </Section>
              <Section title="Products by buyer interest" icon={Handshake} testid="msb-top-products"
                       right={<span className="text-[11px] text-neutral-500">Top 10 in window</span>}>
                <DistList rows={(data.top_products.items || []).map((p) => ({ label: p.product_code, count: p.count }))} empty="No product interest captured." />
              </Section>
              <Section title="Observed Learning Signals" icon={BarChart3} testid="msb-learning"
                       right={<span className="text-[11px] text-neutral-500">Top 6 signals</span>}>
                <div className="text-[11px] text-neutral-500 pb-2 leading-snug">Historical observations from platform conversations only. Never a prediction or guarantee.</div>
                <div className="space-y-2">
                  {(data.learning.items || []).slice(0, 6).map((b, i) => (
                    <div key={i} className="border border-neutral-100 rounded-lg p-2">
                      <div className="flex justify-between items-baseline">
                        <span className="text-[11.5px] font-medium text-neutral-800 truncate">{b.event_type}: {b.event_key || '—'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${b.confidence === 'HIGH' ? 'bg-emerald-50 text-emerald-700' : b.confidence === 'MEDIUM' ? 'bg-amber-50 text-amber-700' : 'bg-neutral-100 text-neutral-600'}`}>{b.confidence}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-x-2 text-[11px] text-neutral-600">
                        <div>Observed: <b>{b.observations}</b></div>
                        <div className="text-emerald-700">Successful: <b>{b.successes}</b></div>
                        <div className="text-red-700">Failed: <b>{b.failures}</b></div>
                        <div>Rate: <b>{b.observed_success_rate_percent == null ? 'n/a' : `${b.observed_success_rate_percent}%`}</b></div>
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-neutral-500">Total events observed: {data.learning.total_events}</div>
                </div>
              </Section>
            </div>

            <div className="text-[11px] text-neutral-400 flex items-center gap-1 pt-1"><Info className="w-3 h-3" /> {data.notes?.attribution}</div>
          </div>
        )
      )}

      {/* INVESTIGATION (SWOT) */}
      {tab === 'swot' && (
        swotLoading || !swot ? (
          <div className="flex items-center justify-center py-16 text-neutral-500 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Diagnosing platform Sales Brain…</div>
        ) : (
          <div className="space-y-5">
            <SwotBlock swot={swot} onSelect={setEvidence} />
            <div>
              <h2 className="text-base font-semibold text-neutral-900 mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-fab-primary" /> Recommended Admin Actions</h2>
              <RecommendedActions actions={swot.recommended_actions} onSelect={(a) => setEvidence({ kind: 'action', ...a })} />
            </div>
          </div>
        )
      )}

      {/* SELLER BRAIN */}
      {tab === 'seller' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Directory — search + status filter + list */}
          <div className="lg:col-span-1 bg-white rounded-xl border border-fab-primary-lighter/70 p-3" data-testid="msb-seller-list">
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={sellerQuery} onChange={(e) => setSellerQuery(e.target.value)} data-testid="msb-seller-search"
                     placeholder="Search seller name..."
                     className="w-full pl-8 pr-2 py-2 text-sm rounded-lg border border-neutral-200 focus:border-fab-primary focus:outline-none" />
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {STATUS_FILTERS.map((f) => (
                <button key={f} onClick={() => setStatusFilter(f)} data-testid={`msb-status-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium border transition-colors ${statusFilter === f ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-neutral-500 border-neutral-200 hover:border-fab-primary/40'}`}>
                  {f === 'ALL' ? 'All' : f}
                </button>
              ))}
            </div>
            {!sellers ? (
              <div className="py-8 text-center text-neutral-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
              <ul className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-100">
                {filteredSellers.map((s) => (
                  <li key={s.seller_id}>
                    <button onClick={() => openSeller(s)} data-testid={`msb-seller-${s.seller_id}`}
                            className={`w-full text-left px-2.5 py-2.5 rounded-lg hover:bg-fab-elevated ${selectedSeller?.seller_id === s.seller_id ? 'bg-fab-primary-lighter/40' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[13px] font-semibold text-neutral-900 truncate">{s.name}</div>
                        <StatusBadge status={s.performance} />
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {s.opportunities} opportunities · {s.conversion_rate_percent == null ? '0% conversion' : `${s.conversion_rate_percent}% conversion`}
                      </div>
                    </button>
                  </li>
                ))}
                {filteredSellers.length === 0 && <li className="py-6 text-center text-neutral-400 text-sm">No sellers match.</li>}
              </ul>
            )}
          </div>

          {/* Selected seller laboratory */}
          <div className="lg:col-span-2 space-y-4" data-testid="msb-seller-detail">
            {!selectedSeller ? (
              <div className="bg-white rounded-xl border border-fab-primary-lighter/70 p-10 text-center text-neutral-500">
                <Brain className="w-8 h-8 mx-auto mb-2 text-neutral-300" /> Select a seller to open its Sales Brain laboratory.
              </div>
            ) : sellerLoading || !sellerDetail ? (
              <div className="flex items-center justify-center py-16 text-neutral-500 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analysing {selectedSeller.name}…</div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-fab-primary-lighter/70 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-lg font-bold text-neutral-900" data-testid="msb-seller-name">{sellerDetail.seller.name}</div>
                      <div className="text-[12px] text-neutral-500">Individual seller AI laboratory</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={sellerDetail.seller.status} testid="msb-seller-status" />
                      <button onClick={() => setSellerInvestigate((v) => !v)} data-testid="msb-seller-investigate"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-fab-primary text-white hover:bg-fab-primary/90">
                        <Activity className="w-3.5 h-3.5" /> {sellerInvestigate ? 'Hide Investigation' : 'Investigate'}
                      </button>
                    </div>
                  </div>

                  {/* Performance cards — seller vs platform */}
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <CmpCard label="Opportunities" c={sellerDetail.comparison.opportunities} testid="opportunities" />
                    <CmpCard label="High Intent" c={sellerDetail.comparison.high_intent} testid="high-intent" />
                    <CmpCard label="AI-Assisted Orders" c={sellerDetail.comparison.ai_assisted_orders} testid="ai-assisted-orders" />
                    <CmpCard label="Conversion" c={sellerDetail.comparison.conversion} testid="conversion" />
                    <CmpCard label="Recommendation Success" c={sellerDetail.comparison.recommendation_success} testid="recommendation-success" />
                    <CmpCard label="Handoffs" c={sellerDetail.comparison.handoffs} testid="handoffs" />
                    <CmpCard label="AI Issues" c={sellerDetail.comparison.ai_issues} testid="ai-issues" />
                    <div className="rounded-lg border border-neutral-100 p-2.5" data-testid="msb-seller-metric-weakest-signal">
                      <div className="text-[10.5px] uppercase tracking-wide text-neutral-500 font-semibold">Weakest Signal</div>
                      <div className="text-[13px] font-semibold text-neutral-900 mt-0.5 truncate">{sellerDetail.metrics.weakest_signal || '—'}</div>
                      <div className="text-[10.5px] text-neutral-400">{sellerDetail.metrics.weakest_signal_rate != null ? `${sellerDetail.metrics.weakest_signal_rate}% success` : 'insufficient data'}</div>
                    </div>
                    <div className="rounded-lg border border-neutral-100 p-2.5" data-testid="msb-seller-metric-best-product">
                      <div className="text-[10.5px] uppercase tracking-wide text-neutral-500 font-semibold">Best Product</div>
                      <div className="text-[13px] font-semibold text-neutral-900 mt-0.5 truncate">{sellerDetail.metrics.best_product || '—'}</div>
                      <div className="text-[10.5px] text-neutral-400">{sellerDetail.metrics.best_product ? 'most recommended' : 'insufficient data'}</div>
                    </div>
                  </div>

                  {/* Sales Brain Health */}
                  <div className="mt-4 flex items-center gap-3 border-t border-neutral-100 pt-3">
                    <span className="text-sm font-semibold text-neutral-700">Sales Brain Health</span>
                    <StatusBadge status={sellerDetail.health_status} testid="msb-seller-health" />
                    <span className="text-[12px] text-neutral-500">{sellerDetail.anomaly_count} area(s) flagged</span>
                  </div>
                </div>

                {/* SWOT with per-section Show More */}
                {sellerInvestigate ? (
                  <>
                    <SellerSwot swot={sellerDetail.swot} onSelect={setEvidence} />
                    <div>
                      <h2 className="text-base font-semibold text-neutral-900 mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-fab-primary" /> Recommended Actions</h2>
                      <SellerActions actions={sellerDetail.swot.recommended_actions} onSelect={(a) => setEvidence({ kind: 'action', ...a })} />
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl border border-dashed border-fab-primary-lighter/70 p-5 text-center text-[13px] text-neutral-500" data-testid="msb-seller-investigate-hint">
                    Click <span className="font-semibold text-fab-primary">Investigate</span> to generate this seller's evidence-based SWOT and recommended actions.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <EvidenceModal item={evidence} onClose={() => setEvidence(null)} />
    </div>
  );
}
